import React, { useEffect, useRef, useState } from "react";
import { getMerchant } from "../../../../helpers/ReduxHelpers";
import { findInObjArr } from "../../../../helpers/UtilityHelper";
import {
    apiConstants,
    post,
    get,
    postMultipart,
    getDownload,
} from "../../../../common/constants";
import "../location.css";
import Loader from "../../../../common/Loader";
import {
    Button,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    Alert,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { Box } from "@mui/material";
import ProzoDataGrid from "../../../../common/ProzoDataGrid";
import { useSnackbar } from "notistack";
import { AddCircleRounded, Delete, DownloadRounded, Edit, UploadFileRounded } from "@mui/icons-material";
import ConfirmDialog from "../../../../common/ConfirmDialog";
import AddPickupLocation from "./AddPickupLocationV2";
import { FilterLocation } from "../Filter";
import { LoadingButton } from "@mui/lab";
import { primaryBtnSx, secondaryBtnSx, dataGridWrapSx } from '../../../../styles/patterns';
import { primaryTextSx, secondaryTextSx, cellStackSx } from '../../../orders/list2/cellStyles';
import { FONT, SIZE, WEIGHT } from '../../../../styles/foundation/typography';
import { SUCCESS, ERROR, SUBTLE, NAVY } from '../../../../styles/foundation/colors';


const getPickupStatusChipSx = (status) => {
    const color = status === 'ACTIVE' ? SUCCESS : ERROR;
    return {
        fontFamily: FONT,
        fontWeight: WEIGHT.semibold,
        fontSize: 11,
        height: 24,
        bgcolor: color + '18',
        color,
        border: `1px solid ${color}40`,
        '& .MuiChip-label': { px: 1 },
    };
};

const getDefaultChipSx = (isDefault) => {
    const color = isDefault ? SUCCESS : SUBTLE;
    return {
        fontFamily: FONT,
        fontWeight: WEIGHT.semibold,
        fontSize: 11,
        height: 24,
        bgcolor: color + '18',
        color,
        border: `1px solid ${color}40`,
        '& .MuiChip-label': { px: 1 },
    };
};

const getDeletedChipSx = (isDeleted) => {
    const color = isDeleted ? ERROR : SUCCESS;
    return {
        fontFamily: FONT,
        fontWeight: WEIGHT.semibold,
        fontSize: 11,
        height: 24,
        bgcolor: color + '18',
        color,
        border: `1px solid ${color}40`,
        '& .MuiChip-label': { px: 1 },
    };
};


export function MerchantLocations({ shipmentType = "B2B" }) {
    const [show, setShow] = useState(false);
    const [locationList, setLocation] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sampleLoading, setSampLoading] = useState(false);
    const [currentId, setCurrentId] = useState();
    const [page, setPage] = useState(0);
    const [reload, setReload] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [filteredLocationList, setFilteredLocationList] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const bulkUploadRef = useRef();
    const { enqueueSnackbar } = useSnackbar();

    const existingLocationNames = filteredLocationList
    .map(location => location.name?.toLowerCase().trim());



    const handleClose = () => {
        setShow(false);
        setCurrentId(null);
    };

    const handleShow = () => setShow(true);
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''

    const loadLocation = () => {
        setLoading(true);
        get(`${apiConstants.BASE_LOCAL}${apiConstants.GET_MERCHANT}/${merchantId}`)
            .then((response) => {
                setLoading(false);
                if (response.data.pickup_details.length > 0) {
                    const filteredLocations = response.data.pickup_details.filter((item) => item.shipmentType === shipmentType)
                    setLocation(filteredLocations)
                } else {
                    setLocation([]);
                }
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    };


    const handleFilter = (filterValues) => {
        const { contactNumber, name } = filterValues
        const filteredLocations = locationList.filter((location) => {
            const matchesContact = contactNumber ? location.contactNumber?.includes(contactNumber) : true
            const matchesName = name ? location.name?.toLowerCase().includes(filterValues.name.toLowerCase()) : true
            return matchesContact && matchesName
        })
        setFilteredLocationList(filteredLocations)
    };

    const toggleStatus = (id, status) => {
        setCurrentId(null);
        setLoading(true);
        const result = findInObjArr(locationList, id, "id");
        const addressCopy = Object.assign({}, result, {
            status: status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
        });
        post(
            `${apiConstants.BASE_LOCAL}${apiConstants.ADD_LOCATION}/${merchantId}`,
            addressCopy
        )
            .then((response) => {
                setLoading(false);
                handleClose();
                loadLocation();
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    };

    const handleEditClick = (val) => {
        setCurrentId(val);
        handleShow();
    };

    const handleDownloadSampleClick = () => {
        setSampLoading(true);
        getDownload(
            `${apiConstants.BASE_LOCAL}${apiConstants.DOWNLOAD_SAMPLE_LOCATION}?fileName=MERCHANT_PICKUP`
        )
            .then((response) => {
                setSampLoading(false);
                const binaryData = [];
                binaryData.push(response.data);
                const href = URL.createObjectURL(
                    new Blob(binaryData, { type: "application/octet-stream" })
                );
                const link = document.createElement("a");
                link.href = href;
                link.setAttribute("download", "pickup-location.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
            .catch((err) => {
                console.error(err);
                setSampLoading(false);
            });
    };

    const handleBulkUpload = (e) => {
        const param = e.target.files[0];
        const formData = new FormData();
        formData.append("file", param);
        formData.append("fileName", param.name);
        setLoading(true);

        postMultipart(`${apiConstants.UPLOAD_LOCATION}/${merchantId}`, formData)
            .then((response) => {
                setLoading(false);
                const failedOrdersCount = response.data?.failedOrders?.length || 0;
                const successOrdersCount = response.data?.successOrders?.length || 0;

                if (failedOrdersCount > 0 && successOrdersCount > 0) {
                    enqueueSnackbar(
                        `${failedOrdersCount} Failed Orders & ${successOrdersCount} Success Orders`,
                        { variant: "success" }
                    );
                } else if (failedOrdersCount > 0 && successOrdersCount === 0) {
                    enqueueSnackbar(`${failedOrdersCount} Failed Orders. No successfull uploads.`, {
                        variant: "error",
                    });
                } else if (failedOrdersCount === 0 && successOrdersCount > 0) {
                    enqueueSnackbar(`${successOrdersCount} Orders successfully uploaded!`, {
                        variant: "success",
                    });
                } else {
                    enqueueSnackbar("No orders were uploaded. Please try again.", {
                        variant: "error",
                    });
                }

                if (bulkUploadRef.current) {
                    bulkUploadRef.current.value = null;
                }
            })
            .catch((err) => {
                enqueueSnackbar("Could not be uploaded!", { variant: "error" });
                setLoading(false);
                if (bulkUploadRef.current) {
                    bulkUploadRef.current.value = null;
                }
            });
    };

    const toggleDelete = (id) => {
        setCurrentId(id);
        setDeleteConfirm(true);
    };

    const handleDelete = (id) => {
        setLoading(true);
        const result = findInObjArr(locationList, id, "id");
        const addressCopy = Object.assign({}, result, { is_deleted: '1' }, { status: "INACTIVE" });
        post(
            `${apiConstants.BASE_LOCAL}${apiConstants.ADD_LOCATION}/${merchantId}`,
            addressCopy
        )
            .then((response) => {
                setLoading(false);
                handleClose();
                setCurrentId()
                loadLocation();
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                setCurrentId()
            });
    };


    useEffect(() => {
        loadLocation();
    }, [reload, shipmentType]);

    const handleAddClick = () => {
        setCurrentId(null);
        handleShow();
    };



    useEffect(() => {
        setFilteredLocationList(locationList);
    }, [locationList]);

    const resetFilter = () => {
        setFilteredLocationList(locationList);
    };

    const isDisabled = (row) => {
        return row.is_deleted !== undefined && row.is_deleted !== '0'
    }

    const columns = [
        {
            headerName: "Name",
            field: "name",
            flex: 1,
            minWidth: 160,
            renderCell: ({ row }) => (
                <Typography sx={primaryTextSx}>{row.name}</Typography>
            ),
        },
        {
            headerName: "Address",
            field: "pincode",
            flex: 1.5,
            minWidth: 240,
            renderCell: ({ row }) => {
                const addArr = [];
                if (row.address_line) addArr.push(row.address_line);
                if (row.city) addArr.push(row.city);
                if (row.state) addArr.push(row.state);
                if (row.pincode) addArr.push(row.pincode);
                if (row.country) addArr.push(row.country);
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={primaryTextSx}>
                            {row.address_line || '-'}
                        </Typography>
                        <Typography sx={secondaryTextSx}>
                            {[row.city, row.state, row.pincode, row.country].filter(Boolean).join(', ')}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            headerName: "Email & Contact No",
            field: "email",
            flex: 1,
            minWidth: 200,
            renderCell: ({ row }) => {
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={primaryTextSx}>{row.email || '-'}</Typography>
                        <Typography sx={secondaryTextSx}>{row.contactNumber || '-'}</Typography>
                    </Box>
                );
            }
        },
        {
            headerName: "GSTIN",
            field: "gstin",
            flex: 0.8,
            minWidth: 150,
            renderCell: ({ row }) => (
                <Typography sx={primaryTextSx}>{row.gstin || '-'}</Typography>
            ),
        },
        {
            headerName: "Status",
            field: "status",
            flex: 0.7,
            minWidth: 110,
            renderCell: ({ row }) => {
                return (
                    <Chip
                        sx={getPickupStatusChipSx(row.status)}
                        label={row.status}
                        size="small"
                    />
                );
            },
        },
        {
            headerName: "Is Default",
            field: "is_default",
            flex: 0.5,
            minWidth: 100,
            renderCell: ({ row }) => {
                const isDefault = row.is_default === true;
                return (
                    <Chip
                        sx={getDefaultChipSx(isDefault)}
                        label={isDefault ? "Yes" : "No"}
                        size="small"
                    />
                );
            },
        },
        {
            headerName: "Deleted",
            field: "is_deleted",
            flex: 0.5,
            minWidth: 100,
            renderCell: ({ row }) => {
                const deleted = isDisabled(row);
                return (
                    <Chip
                        sx={getDeletedChipSx(deleted)}
                        label={deleted ? 'Yes' : 'No'}
                        size="small"
                    />
                );
            },
        },
        {
            headerName: "Action",
            field: "action",
            flex: 0.8,
            minWidth: 150,
            renderCell: ({ row }) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={`${row.status === "ACTIVE" ? "Deactivate" : "Activate"} Pickup Location`}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={row.status === "ACTIVE"}
                                        color="primary"
                                        size="small"
                                        disabled={isDisabled(row)}
                                        onChange={() => toggleStatus(row.id, row.status)}
                                    />
                                }
                                label=""
                                sx={{ mr: 0 }}
                            />
                        </Tooltip>
                        <Tooltip title="Edit Pickup Location">
                            <IconButton onClick={() => handleEditClick(row)}
                                disabled={isDisabled(row)}
                                size="small"
                            >
                                <Edit
                                    color={isDisabled(row) ? "disabled" : "primary"}
                                    fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Pickup Location">
                            <IconButton
                                onClick={() => toggleDelete(row.id, row.is_deleted)}
                                disabled={isDisabled(row)}
                                size="small"
                            >
                                <Delete
                                    color={isDisabled(row) ? "disabled" : "primary"}
                                    fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    return (
        <>
            {loading && <Loader />}
            <input
                type="file"
                onChange={handleBulkUpload}
                ref={bulkUploadRef}
                style={{ display: "none" }}
            />
            {shipmentType === "B2C" && (
                <Alert severity="info" sx={{borderRadius: '8px', mb: 1, '& .MuiAlert-message': {fontFamily: 'Poppins, sans-serif', fontSize: '0.82rem'}}}>
                    These are your <strong>D2C Freight</strong> pickup locations. They are registered separately from PTL Freight locations. To manage PTL pickup locations, switch to the PTL tab above.
                </Alert>
            )}
            {shipmentType === "B2B" && (
                <Alert severity="info" sx={{borderRadius: '8px', mb: 1, '& .MuiAlert-message': {fontFamily: 'Poppins, sans-serif', fontSize: '0.82rem'}}}>
                    These are your <strong>PTL Freight</strong> pickup locations. They must be registered with the courier partner before use.
                </Alert>
            )}
            <Box
                sx={{
                    justifyContent: "flex-end",
                    width: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 1,
                    mb: 2,
                }}
            >
                <Button
                    startIcon={<AddCircleRounded />}
                    type="button"
                    onClick={handleAddClick}
                    variant="contained"
                    size="small"
                    sx={primaryBtnSx}
                >
                    Add
                </Button>
                <Button
                    startIcon={<UploadFileRounded />}
                    type="button"
                    onClick={() => bulkUploadRef.current.click()}
                    variant="outlined"
                    size="small"
                    sx={secondaryBtnSx}
                >
                    Upload
                </Button>
                <LoadingButton
                    startIcon={<DownloadRounded />}
                    type="button"
                    loading={sampleLoading}
                    onClick={handleDownloadSampleClick}
                    variant="outlined"
                    size="small"
                    sx={secondaryBtnSx}
                >
                    Sample Download
                </LoadingButton>
            </Box>

            <FilterLocation onFilter={handleFilter} resetFilter={resetFilter} />

            <Box sx={{ ...dataGridWrapSx, mt: 2 }}>
                <ProzoDataGrid
                    rows={filteredLocationList.length > 0 ? filteredLocationList : []}
                    getRowId={(row) => row.name}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    rowCount={filteredLocationList?.length || 0}
                    paginationMode={'client'}
                    columns={columns}
                />
            </Box>

            <AddPickupLocation
                show={show}
                setShow={setShow}
                merchantId={merchantId}
                setReload={setReload}
                reload={reload}
                handleClose={handleClose}
                currentId={currentId}
                shipmentType={shipmentType}
                existingLocationNames={existingLocationNames}
            />
            <ConfirmDialog
                title=''
                open={deleteConfirm}
                setOpen={setDeleteConfirm}
                onConfirm={() => handleDelete(currentId)}
            >
                Are you sure you want to delete Pickup Location?
            </ConfirmDialog>
        </>
    );
}

export default MerchantLocations
